import config from '../../config';
import { UpdateProfilePayload } from './UpdateProfilePayload';
import FLSApiClient from '../../common/FLSApiClient';
import { ProfileInfo } from '../../components/common/props-types';
import FamiliplusProfile from 'pages/familiplus/FamiliplusProfile';
import { UpdateNewsletterPrefsPayload } from './UpdateNewsletterPrefsPayload';

export const ProfileExists = (email: string, recaptchaToken: string): Promise<any> => {
  const domain = config.FLS_API_DOMAIN;
  const requestOptions = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', RecaptchaToken: recaptchaToken },
  };

  const cleanEmail = encodeURIComponent(email);
  return fetch(domain + '/api/Profile/Exists?Email=' + cleanEmail, requestOptions);
};

export const ResetPassword = (email: string): Promise<any> => {
  const cleanEmail = encodeURIComponent(email);
  return FLSApiClient.post('/api/Profile/ResetPassword?Email=' + cleanEmail);
};

export const GetProfile = (sessionId: string): Promise<any> => {
  return FLSApiClient.get('/api/Profile', {
    params: {
      sessionId: sessionId,
    },
  }).then((response) => {
    return response.data;
  });
};

export const GetProfileInfo = async (profileAuth: { sessionId: string; token: string }): Promise<ProfileInfo> => {
  const profile = await GetProfile(profileAuth.sessionId);
  const profileInfo: ProfileInfo = {
    birthday:
      profile.BirthDateYear && profile.BirthDateMonth && profile.BirthDateDay
        ? new Date(profile.BirthDateYear, profile.BirthDateMonth - 1, profile.BirthDateDay)
        : undefined,
    language: profile.Language,
    gender: profile.Gender,
    lastName: profile.Lastname,
    firstName: profile.Firstname,
    email: profile.Email,
    homePhone: profile.Phones?.OtherPhone,
    mobilePhone: profile.Phones?.CellPhone,
    familiplusCard: profile.Card?.CardNumber,
    familiplusCardPoints: profile.Card?.Points,
    ereceiptPreference: profile.EReceiptPreference,
    smsConsent: profile.Consents.Sms,
    newsletterConsent: profile.Consents.Newsletter,
    profilingConsent: profile.Consents.Profiling,
    oldProfileWasNewsletter: false,
  };
  return profileInfo;
};

export const UpdateProfile = (sessionId: string, payload: UpdateProfilePayload): Promise<any> => {
  return FLSApiClient.put('/api/Profile', {
    SessionId: sessionId,
    ...payload,
  });
};

export const UpdatePartialProfile = (sessionId: string, profile: ProfileInfo): Promise<any> => {
  const birthDay = profile.birthday ? profile.birthday.getUTCDate() : 0;
  const birthMonth = profile.birthday ? profile.birthday.getUTCMonth() + 1 : 0;
  const birthYear = profile.birthday ? profile.birthday.getUTCFullYear() : 0;

  const SmsConsent = profile.smsConsent != true ? null : true;
  const ProfilingConsent = profile.profilingConsent != true ? null : true;

  const payload = {
    BirthDateYear: birthYear,
    BirthDateMonth: birthMonth,
    BirthDateDay: birthDay,
    Gender: profile.gender,
    Lastname: profile.lastName,
    Firstname: profile.firstName,
    Language: profile.language,
    Phones: {
      CellPhone: profile.mobilePhone,
      OtherPhone: profile.homePhone,
    },
    EReceiptPreference: profile.ereceiptPreference,
    //Dans le register, si le consentement sms et profiling n'est pas donner on le set a null pour que sa ne modifie pas le not given
    //Dans le UpdateProfil non partial sa va pouvoir etre false si la valeur precedente etait a true vu qu'on va avoir le consentement precedent
    SmsConsent: SmsConsent,
    ProfilingConsent: ProfilingConsent,
  };

  return FLSApiClient.put('/api/Profile', {
    SessionId: sessionId,
    ...payload,
  });
};

export const CreateVirtualCard = (
  profileAuth: { sessionId: string; token: string },
  updateNewsletterConsent: boolean,
  familiplusTCConsent: boolean
): Promise<any> => {
  const domain = config.FLS_API_DOMAIN;
  const requestOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + profileAuth.token },
    body: JSON.stringify({
      SessionId: profileAuth.sessionId,
      UpdateNewsletterConsent: updateNewsletterConsent,
      ConsentFamiliplusTC: familiplusTCConsent,
    }),
  };
  return fetch(domain + '/api/Card/VirtualCard', requestOptions);
};

export const GiveToOES = (profileAuth: { sessionId: string; token: string }, nbrPointsGiven: number): Promise<any> => {
  const domain = config.FLS_API_DOMAIN;
  const requestOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + profileAuth.token },
    body: JSON.stringify({
      SessionId: profileAuth.sessionId,
      Points: nbrPointsGiven,
    }),
  };

  return fetch(domain + '/api/Partner/GiveToOES', requestOptions);
};

export const GetFamiliplusProfile = async (sessionId: string): Promise<FamiliplusProfile> => {
  const responseBody = await GetProfile(sessionId);

  const familiplusProfile: FamiliplusProfile = {
    Firstname: responseBody.Firstname,
    Lastname: responseBody.Lastname,
    Email: responseBody.Email,
    Card: responseBody.Card?.CardNumber
      ? {
          CardNumber: responseBody.Card?.CardNumber,
          Points: responseBody.Card?.Points || 0,
          CashValue: responseBody.Card?.CashValue || 0,
        }
      : undefined,
    ProfileCompletedStatus: responseBody.ProfileCompletedStatus,
  };

  return familiplusProfile;
};

export const GetNewsletterPrefs = (sessionId: string): Promise<any> => {
  return FLSApiClient.get('/api/Profile/ContentType', {
    params: {
      sessionId: sessionId,
    },
  });
};

export const UpdateNewsletterPrefs = (sessionId: string, payload: UpdateNewsletterPrefsPayload): Promise<any> => {
  return FLSApiClient.put('/api/Profile/ContentType', {
    SessionId: sessionId,
    ...payload,
  });
};

export const GetMfaSmsStatus = (auth0UserId: string): Promise<boolean> => {
  return FLSApiClient.get('/api/Profile/IsMfaSmsActive', { params: { auth0UserId } });
};

export const ResetMfaSms = (auth0UserId: string): Promise<void> => {
  return FLSApiClient.post('/api/Profile/ResetMfaSms', null, { params: { auth0UserId } });
};

export const SendActivationEmail = (email: string): Promise<any> => {
  const domain = config.FUAS_API_DOMAIN || '';

  return fetch(domain + '/api/Account/SendActivationEmailFromAuth0?email=' + encodeURIComponent(email), {
    method: 'POST',
    mode: 'no-cors',
  });
};
